import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";
import Step1Form from "./death-claim/components/policyInformation/Step1Form";
import Step2Form from "./death-claim/components/policyInformation/Step2Form";
import Deceased from "./death-claim/components/detailsOfDeceased/Deceased";
import BankingInfo from "./death-claim/components/bankingInformation/BankingInfo";
import SubmissionSuccess from "./common/submission/SubmissionSuccess";
import { string } from "../../../constants/string";
import WarningPopup from "../../../components/WarningPopup";
import { useAuthStore } from "../../../store/useAuthStore";
import {
  createClaim,
  createClaimForTenPercent,
  getPolicy,
} from "../../../services/guest";
import { usePolicyStore } from "../../../store/usePolicyStore";
import { useClaimFormStore } from "../../../store/useClaimFormStore";
import ClaimantPersonalInfo from "./death-claim/components/claimantInformation/ClaimantPersonalInfo";
import Loader from "../../../components/Loader";
import DocumentVerification from "./components/documentVerification/DocumentVerification";
import { useClaimTypeStore } from "../../../store/useClaimTypeStore";
import { useTenPercentClaimFormStore } from "../../../store/useTenPercentClaimFormStore";
import DeclinedDraftPopup from "./death-claim/components/elegibiltyCheckPopups/DeclinedDraftPopup";
import VerifiedDraftPopup from "./death-claim/components/elegibiltyCheckPopups/VerfiedDraftPopup";
import PolicyDetails from "../../underwriter/forms/PolicyDetails";
import { useToast } from "../../../hooks/useToast";
import ClaimBenefit from "./death-claim/components/claimBenefitSchedule/ClaimBenefit";
import PersonalInformation from "./components/personalInformation/PersonalInformation";
import TenpercentBanking from "./components/tenpercentBanking/TenpercentBanking";
import HomeIcon from "@mui/icons-material/Home";
import PolicyIcon from "@mui/icons-material/Description";
import { useClaimantFormStore } from "../../../store/useClaimantFormStore";
import { usePersonalInfoFormStore } from "../../../store/usePersonalInfoFormStore";
import { useTenPercentBankDetailsStore } from "../../../store/useTenPercentBankDetailsStore";
import { usePolicyNumberStore } from "../../../store/usePolicyNumberStore";
import { useVerifiedStore } from "../../../store/useVerifiedStore";
import { useDeceasedFormStore } from "../../../store/useDeceasedFormStore";
import { useBankFormStore } from "../../../store/useBankFormStore";

type PolicyProps = {
  onSubmit: () => void;
  compactLayout?: boolean;
};

export default function PolicyInformation(props: PolicyProps) {
  const { onSubmit, compactLayout } = props;
  const typeOfClaim = useClaimTypeStore((state) => state.claimType);
  const isDeathClaim = typeOfClaim === "DEATH";
  const theme = useTheme();
  const [step, setStep] = useState(1);
  const [showWarning, setShowWarning] = useState(false);
  const [warningError, setWarningError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showNotElegible, setShowNotElegible] = useState(false);
  const [showElegible, setShowElegible] = useState(false);
  const [eligibilityFlags, setEligibilityFlags] = useState({
    claimAlreadyPresent: false,
    fiveYearsActive: false,
    policyLapsed: false,
  });
 
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setPolicyData = usePolicyStore((state) => state.setPolicyData);
  const policyData = usePolicyStore((state) => state.policyData);
  const addTenPercentDocument = useTenPercentClaimFormStore(
    (state) => state.addTenPercentDocument
  );
  const setPolicyNumber = usePolicyNumberStore(
      (state) => state.setPolicyNumber
    );
    const setShowVerified = useVerifiedStore((state) => state.setShowVerified);

  const { showInfoToast } = useToast();

  const { setPolicyInfo, setClaimant, setDeceased, setBankingInfo } =
    useClaimFormStore();

  const {
    setTenPercentPersonalInfoDto,
    setTenPercentBankingInfoDto,
    getTenPercentFullPayload,
  } = useTenPercentClaimFormStore();

  const handleContinue = () => {
    setStep((prev) => prev + 1);
  };

  const handlePolicyNumberNext = async (policyNumber?: string) => {
    if (policyNumber) {
      setIsLoading(true);

      const res = await getPolicy(typeOfClaim, policyNumber);

      if (res.status === 200) {
        const data = res.data;

        if (
          data?.is_eligibility_status?.claimAlreadyPresent !== undefined &&
          data?.is_eligibility_status?.fiveYearsActive !== undefined &&
          data?.is_eligibility_status?.policyLapsed !== undefined
        ) {
          const flags = {
            claimAlreadyPresent:
              data.is_eligibility_status.claimAlreadyPresent ?? false,
            fiveYearsActive:
              data.is_eligibility_status.fiveYearsActive ?? false,
            policyLapsed: data.is_eligibility_status.policyLapsed ?? false,
          };

          setEligibilityFlags(flags);

          const isEligible =
            flags.claimAlreadyPresent &&
            flags.fiveYearsActive &&
            flags.policyLapsed;

          if (isEligible) {
            setShowElegible(true);
            setPolicyData(data);
          } else {
            setShowNotElegible(true);
          }
        } else {
          if (isDeathClaim) {
            setStep((prev) => prev + 1);
          } else {
            setShowElegible(true);
          }
          setPolicyData(data);
        }
      } else if (res.status === 404) {
        setWarningError(res.data.message);
        setShowWarning(true);
      } else {
        const errorMessage = res?.data?.message;

        if (errorMessage?.includes("already submitted")) {
          setWarningError("Claim already submitted for this policy");
        } else if (errorMessage?.includes("Policy not found")) {
          setWarningError("Invalid policy number. Please check and try again.");
        } else {
          setWarningError(errorMessage);
        }

        setShowWarning(true);
      }
      setIsLoading(false);
    }
  };

  const handlePolicyInfoNext = (policyInfoData: any) => {
    setPolicyInfo(policyInfoData);
    setStep((prev) => prev + 1);
  };

  const handleTenPercentPolicyInfoNext = (personalInfoData: any) => {
    setTenPercentPersonalInfoDto(personalInfoData);
    setStep((prev) => prev + 1);
  };

  const handleTenPercentDocumentVerificationNext = (
    key: any,
    documentVerificationData: any
  ) => {
    addTenPercentDocument(key, documentVerificationData);
    setStep((prev) => prev + 1);
  };

  const handleTenPercentBankingInfoNext = async (
    tenPercentBankDetails: any
  ) => {
    setTenPercentBankingInfoDto(tenPercentBankDetails);
    setIsLoading(true);

    const payload = getTenPercentFullPayload();
    const formData = new FormData();

    // 1. Append the main JSON payload
    const claimBlob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });
    formData.append("claim", claimBlob);

    // 2. Append each file if present
    const documents =
      useTenPercentClaimFormStore.getState().tenPercentdocuments;

    if (documents.identity_document)
      formData.append("identity_document", documents.identity_document);
    if (documents.selfie_with_identity)
      formData.append("selfie_with_identity", documents.selfie_with_identity);
    if (documents.liveness_check)
      formData.append("liveness_check", documents.liveness_check);
    if (documents.home_affairs_verification)
      formData.append(
        "home_affairs_verification",
        documents.home_affairs_verification
      );
    if (documents.bank_proof)
      formData.append("bank_proof", documents.bank_proof);

    // 3. Submit to backend
    const res = await createClaimForTenPercent(formData);

    if (res.status === 201) {
      setPolicyData(null)
      setPolicyNumber(null)
       useTenPercentClaimFormStore.getState().reset();
       usePersonalInfoFormStore.getState().resetPersonalInfoForm();
       useTenPercentBankDetailsStore.getState().resetTenPercentBankDetails()
      setStep((prev) => prev + 1);
     
    } else {
      showInfoToast(res.data.message);
    }

    setIsLoading(false);
  };

  const handleClaimantInfoNext = (claimantData: any) => {
    setClaimant(claimantData);
    setStep((prev) => prev + 1);
  };

  const handleDeceasedNext = (deceasedData: any) => {
    setDeceased(deceasedData);
    setStep((prev) => prev + 1);
  };

  const handleBenefitNext = (claimBenefitData: any) => {
    setStep((prev) => prev + 1);
  };

  const handleBankingInfoNext = async (bankData: any) => {
    setBankingInfo(bankData);

    setIsLoading(true);
    const payload = useClaimFormStore.getState().getFullPayload();
    const formData = new FormData();
    const claimBlob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });

    formData.append("claim", claimBlob);
    const { documents } = useClaimFormStore.getState();
    Object.entries(documents).forEach(([key, file]) => {
      if (file instanceof File) formData.append(key, file);
    });

    const res = await createClaim(formData);
    if (res.status === 200) {
      setPolicyData(null)
      setPolicyNumber(null)
      useClaimFormStore.getState().reset();
      useClaimantFormStore.getState().resetClaimantForm();
      useDeceasedFormStore.getState().resetDeceasedDetails();
      useBankFormStore.getState().resetBankingInfo();
      setShowVerified(false)
      setStep((prev) => prev + 1);
    } else {
      showInfoToast(res.data.message);
    }
    setIsLoading(false);
  };

  const handleHomeClick = () => {
    window.location.href = "/";
  };

  const handleOnsubmit = () => {
    onSubmit();
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };


  const renderStepContent = () => {
    if (isDeathClaim) {
      switch (step) {
        case 1:
          return <Step1Form handlePolicyNumberNext={handlePolicyNumberNext} />;
        case 2:
          return (
            <Step2Form
              handlePolicyInfoNext={handlePolicyInfoNext}
              handleBack={handleBack}
            />
          );
        case 3:
          return (
            <ClaimantPersonalInfo
              handleClaimantInfoNext={handleClaimantInfoNext}
              handleBack={handleBack}
            />
          );
        case 4:
          return (
            <Deceased
              handleDeceasedNext={handleDeceasedNext}
              handleBack={handleBack}
            />
          );
        case 5:
          return (
            <ClaimBenefit
              handleBenefitNext={handleBenefitNext}
              handleBack={handleBack}
            />
          );
        case 6:
          return (
            <BankingInfo
              handleBankingInfoNext={handleBankingInfoNext}
              handleBack={handleBack}
            />
          );
        case 7:
          return (
            <SubmissionSuccess
              onHomeClick={isAuthenticated ? handleOnsubmit : handleHomeClick}
            />
          );
      }
    } else {
      // Ten Percent Flow (No Step2Form)
      switch (step) {
        case 1:
          return <Step1Form handlePolicyNumberNext={handlePolicyNumberNext} />;
        case 2:
          return (
            <PersonalInformation
              handleTenPercentPolicyInfoNext={handleTenPercentPolicyInfoNext}
              handleBack={handleBack}
            />
          );
        case 3:
          return (
            <DocumentVerification
              handleNext={() => setStep((prev) => prev + 1)}
              handleBack={handleBack}
              handleTenPercentDocumentVerificationNext={
                handleTenPercentDocumentVerificationNext
              }
            />
          );
        case 4:
          return (
            <TenpercentBanking
              handleBankingInfoNext={handleBankingInfoNext}
              handleBack={handleBack}
              handleTenPercentBankingInfoNext={handleTenPercentBankingInfoNext}
            />
          );
        case 5:
          return (
            <SubmissionSuccess
              onHomeClick={isAuthenticated ? handleOnsubmit : handleHomeClick}
            />
          );
      }
    }

    return null;
  };

  const getTitle = () => {
    if (isDeathClaim) {
      switch (step) {
        case 1:
        case 2:
          return string.claims.policyInformation;
        case 3:
          return string.claims.claimantPersonalInformation;
        case 4:
          return string.claims.detailsOfDeceased;
        case 5:
          return "SCHEDULE CLAIM BENEFIT";
        case 6:
          return string.claims.bankingInformation;
        default:
          return "";
      }
    } else {
      switch (step) {
        case 1:
          return string.claims.policyInformation;
        case 2:
          return "PERSONAL INFORMATION";
        case 3:
          return "DOCUMENT VERIFICATION";
        case 4:
          return string.claims.bankingInformation;
        default:
          return "";
      }
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: compactLayout ? 3 : { xs: 2, md: 15, lg: 18 },
        flexDirection: "column",
        alignItems: "center",
        mt: 4,
      }}
    >
      {policyData?.policy_number && (
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={{
            alignSelf: "flex-start",
            mb: 2,
            ml: compactLayout ? 1 : 0,
          }}
        >
           <Typography
            color="text.primary"
            sx={{ display: "flex", alignItems: "center" }}
            fontSize={16}
            fontWeight="bold"
          >
           
            {isDeathClaim ? "Death Claim": "10% Claim"}
          </Typography>

          <Typography
            color="text.primary"
            sx={{ display: "flex", alignItems: "center" }}
            fontSize={16}
            fontWeight="bold"
          >
            {policyData.policy_number}
          </Typography>
        </Breadcrumbs>
      )}

      <Typography
        variant="h4"
        sx={{
          color: theme.palette.primary.main,
          fontSize: { xs: "28px", md: "38px" },
          fontWeight: 600,
          mb: 3,
          textAlign: "center",
        }}
      >
        {getTitle()}
      </Typography>
      {isLoading ? <Loader isLoading={isLoading} /> : null}

      {renderStepContent()}

      <WarningPopup
        open={showWarning}
        onClose={() => setShowWarning(false)}
        warningError={warningError}
      />
      <DeclinedDraftPopup
        open={showNotElegible}
        onClose={() => setShowNotElegible(false)}
        label="Eligibility Not Met"
        text="Unfortunately, your policy does not meet all the requirements for automatic claim processing at this time. Here’s what we checked:"
        flags={eligibilityFlags}
      />
      <VerifiedDraftPopup
        handleContinue={handleContinue}
        open={showElegible}
        onClose={() => setShowElegible(false)}
        label="Congratulations!"
        text="You’ve successfully met all the criteria for 10% claim eligibility. Here's a summary of your qualifications."
      />
    </Box>
  );
}
